import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    CheckCircle2,
    Send,
    Loader2,
    AlertCircle,
    Phone as PhoneIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { BTN, BRAND } from "@/lib/design";
import { fade } from "../_shared/motion";
import { REFERRAL_SOURCE_OPTIONS, URGENCY, INITIAL_FORM } from "./constants";

export default function ReferralForm() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.youth_name || !form.referrer_name || !form.email || !form.phone) {
            toast.error("Please fill in the youth's name, your name, email and phone.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                youth_age: form.youth_age ? parseInt(form.youth_age, 10) : null,
            };
            await api.post("/referral", payload);
            setSubmitted(true);
            setForm(INITIAL_FORM);
            toast.success("Referral received — our team will reach out shortly.");
        } catch (err) {
            toast.error("Something went wrong. Please call " + BRAND.phone + ".");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="py-16 md:py-20 bg-[#EFEBE0]" id="referral-form">
            <div className="max-w-4xl mx-auto px-6">
                <motion.div {...fade} className="bg-white rounded-3xl border border-[#E5DFD3] p-8 md:p-12">
                    <div className="mb-8">
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                            Start a referral
                        </p>
                        <h2 className="mt-3 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                            Tell us about the young man.
                        </h2>
                        <p className="mt-3 text-[14.5px] text-[#6F6B63] leading-relaxed">
                            Share only what's comfortable in this form.
                            Detailed records or sensitive documents can
                            be sent securely after we connect.
                        </p>
                    </div>

                    {submitted ? (
                        <SuccessState onReset={() => setSubmitted(false)} />
                    ) : (
                        <FormFields
                            form={form}
                            update={update}
                            setForm={setForm}
                            submitting={submitting}
                            onSubmit={submit}
                        />
                    )}
                </motion.div>

                <div className="mt-8 text-center">
                    <p className="text-[14px] text-[#6F6B63]">
                        Prefer to talk first?{" "}
                        <a
                            href={BRAND.phoneHref}
                            className="text-[#0E2A47] font-medium hover:underline inline-flex items-center gap-1.5"
                            data-testid="referral-call"
                        >
                            <PhoneIcon className="h-3.5 w-3.5" />
                            Call {BRAND.phone}
                        </a>
                    </p>
                </div>
            </div>
        </section>
    );
}

function SuccessState({ onReset }) {
    return (
        <div
            className="rounded-2xl bg-[#E8EFE8] border border-[#0E2A47]/20 p-8 text-center"
            data-testid="referral-success"
        >
            <CheckCircle2 className="h-10 w-10 text-[#0E2A47] mx-auto mb-4" />
            <h3 className="font-[Outfit] text-2xl font-semibold tracking-tight">
                Referral received
            </h3>
            <p className="mt-3 text-[15px] text-[#6F6B63] max-w-lg mx-auto">
                Thank you — a member of our team will reach out within
                one business day. If your situation is urgent, please
                call us directly at {BRAND.phone}.
            </p>
            <button
                type="button"
                onClick={onReset}
                className={`mt-7 ${BTN.outline}`}
                data-testid="referral-submit-another"
            >
                Submit another
            </button>
        </div>
    );
}

function FormFields({ form, update, setForm, submitting, onSubmit }) {
    return (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="referral-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="rtc-label">Youth name *</label>
                    <input
                        required
                        className="rtc-input"
                        value={form.youth_name}
                        onChange={update("youth_name")}
                        data-testid="referral-youth-name"
                    />
                </div>
                <div>
                    <label className="rtc-label">Age</label>
                    <input
                        type="number"
                        min="0"
                        max="21"
                        className="rtc-input"
                        value={form.youth_age}
                        onChange={update("youth_age")}
                        data-testid="referral-age"
                    />
                </div>
                <div>
                    <label className="rtc-label">Parent / guardian</label>
                    <input
                        className="rtc-input"
                        value={form.parent_guardian}
                        onChange={update("parent_guardian")}
                        data-testid="referral-parent"
                    />
                </div>
                <div>
                    <label className="rtc-label">Your full name *</label>
                    <input
                        required
                        className="rtc-input"
                        value={form.referrer_name}
                        onChange={update("referrer_name")}
                        data-testid="referral-referrer-name"
                    />
                </div>
                <div>
                    <label className="rtc-label">Referral source *</label>
                    <select
                        className="rtc-input"
                        value={form.referral_source}
                        onChange={update("referral_source")}
                        data-testid="referral-source"
                    >
                        {REFERRAL_SOURCE_OPTIONS.map((o) => (
                            <option key={o} value={o}>
                                {o}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="rtc-label">Agency / Organization</label>
                    <input
                        className="rtc-input"
                        value={form.agency_name}
                        onChange={update("agency_name")}
                        data-testid="referral-agency"
                    />
                </div>
                <div>
                    <label className="rtc-label">Phone number *</label>
                    <input
                        required
                        className="rtc-input"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="referral-phone"
                    />
                </div>
                <div>
                    <label className="rtc-label">Email *</label>
                    <input
                        required
                        type="email"
                        className="rtc-input"
                        value={form.email}
                        onChange={update("email")}
                        data-testid="referral-email"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="rtc-label">Current concerns</label>
                    <textarea
                        rows={4}
                        className="rtc-input"
                        placeholder="What are the main concerns? What strengths do you see?"
                        value={form.current_concerns}
                        onChange={update("current_concerns")}
                        data-testid="referral-concerns"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="rtc-label">Urgency level</label>
                    <div className="flex flex-wrap gap-2">
                        {URGENCY.map((u) => (
                            <button
                                type="button"
                                key={u.v}
                                onClick={() => setForm((f) => ({ ...f, urgency_level: u.v }))}
                                data-testid={`urgency-${u.v}`}
                                className={`px-4 py-2 rounded-full text-[13.5px] font-medium border transition-colors ${
                                    form.urgency_level === u.v
                                        ? "bg-[#0E2A47] text-white border-[#0E2A47]"
                                        : "bg-white text-[#1A2230] border-[#E5DFD3] hover:border-[#0E2A47]"
                                }`}
                            >
                                {u.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="md:col-span-2">
                    <label className="rtc-label">Additional notes</label>
                    <textarea
                        rows={3}
                        className="rtc-input"
                        value={form.additional_notes}
                        onChange={update("additional_notes")}
                        data-testid="referral-notes"
                    />
                </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-2xl bg-[#EFEBE0] border border-[#E5DFD3]">
                <AlertCircle className="h-4.5 w-4.5 text-[#C8A24A] mt-0.5 shrink-0" />
                <p className="text-[13px] text-[#6F6B63] leading-relaxed">
                    Please avoid sharing Social Security numbers or
                    sensitive medical details here. We'll request those
                    securely after we connect. Admissions are reviewed
                    on a case-by-case basis.
                </p>
            </div>

            <button
                type="submit"
                disabled={submitting}
                className={`${BTN.primary} w-full sm:w-auto`}
                data-testid="referral-submit"
            >
                {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <Send className="h-4 w-4" />
                )}
                Submit referral
            </button>
        </form>
    );
}
